<template>
    <div class="country-selector-wrapper">
        <button type="button" class="country-selector" @click.stop="toggleDropdown">
            <img v-if="getFlagUrl(selectedOption)" :src="getFlagUrl(selectedOption)" :alt="selectedOption.countryCode || 'flag'" class="flag-icon-img" />
            <span v-else class="flag-icon-text">{{ defaultFlagText }}</span>
            <span>{{ selectedOption.code }}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>
        <div v-if="showDropdown" class="country-dropdown">
            <button
                v-for="item in phoneAreaCodes"
                :key="`${item.countryCode}-${item.code}`"
                type="button"
                class="country-option"
                :class="{ selected: item.code === selectedOption.code && item.countryCode === selectedOption.countryCode }"
                @click.stop="selectOption(item)"
            >
                <img v-if="getFlagUrl(item)" :src="getFlagUrl(item)" :alt="item.countryCode || 'flag'" class="country-option-flag-img" />
                <span v-else class="country-option-flag">{{ defaultFlagText }}</span>
                <span class="country-option-name">{{ item.name }}</span>
                <span class="country-option-code">{{ item.code }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { buildApiUrl } from '@/config/api'

const DEFAULT_PHONE_AREA_CODE = {
    name: 'United States, Canada',
    code: '+1',
    countryCode: 'US',
    flagEmoji: ''
}

export default {
    name: 'PhoneAreaCodeSelect',
    props: {
        value: {
            type: Object,
            default: () => DEFAULT_PHONE_AREA_CODE
        }
    },
    data() {
        return {
            defaultOption: DEFAULT_PHONE_AREA_CODE,
            phoneAreaCodes: [],
            showDropdown: false
        }
    },
    computed: {
        selectedOption() {
            return this.value && this.value.code ? this.value : this.defaultOption
        },
        defaultFlagText() {
            return '🌐'
        }
    },
    mounted() {
        this.fetchPhoneAreaCodes()
        document.addEventListener('click', this.handleDocumentClick)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick)
    },
    methods: {
        handleDocumentClick() {
            this.showDropdown = false
        },
        toggleDropdown() {
            this.showDropdown = !this.showDropdown
        },
        selectOption(item) {
            this.$emit('input', item)
            this.showDropdown = false
        },
        getFlagUrl(item) {
            const countryCode = (item?.countryCode || '').trim().toUpperCase()
            if (!/^[A-Z]{2}$/.test(countryCode)) {
                return ''
            }

            return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`
        },
        async fetchPhoneAreaCodes() {
            this.$emit('error-change', '')
            try {
                const response = await axios.get(buildApiUrl('/address/phoneAreaCodes'))
                const data = response.data
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.data)
                        ? data.data
                        : Array.isArray(data?.rows)
                            ? data.rows
                            : []

                if (!list.length) {
                    throw new Error('Invalid phone area code response')
                }

                this.phoneAreaCodes = list
                const matchedDefault = list.find(item => item.code === this.defaultOption.code && item.countryCode === this.defaultOption.countryCode)
                this.$emit('input', matchedDefault || list[0])
            } catch (error) {
                console.error('Fetch phone area codes error:', error)
                this.phoneAreaCodes = [this.defaultOption]
                this.$emit('input', this.defaultOption)
                this.$emit('error-change', 'Area code list unavailable, using default +1.')
            }
        }
    }
}
</script>

<style scoped>
.country-selector-wrapper {
    position: relative;
    flex-shrink: 0;
}

.country-selector {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #333;
    font-weight: 500;
    cursor: pointer;
    flex-shrink: 0;
    border: none;
    background: transparent;
    padding: 0;
}

.flag-icon-text {
    font-size: 16px;
    line-height: 1;
    width: 20px;
    text-align: center;
}

.flag-icon-img {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
}

.country-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 280px;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 20;
}

.country-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: none;
    background: #fff;
    cursor: pointer;
    text-align: left;
}

.country-option.selected,
.country-option:hover {
    background: #fff4f6;
}

.country-option-flag {
    width: 20px;
    text-align: center;
    flex-shrink: 0;
}

.country-option-flag-img {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
}

.country-option-name {
    flex: 1;
    color: #333;
    font-size: 13px;
}

.country-option-code {
    color: #777;
    font-size: 13px;
}
</style>
